import type { HDType } from './types';

export type HDType = 'manifestor' | 'generator' | 'manifesting-generator' | 'projector' | 'reflector';

export interface UserSymptom {
  id: string;
  userId: string;
  text: string;
  description?: string;
  energyType: HDType;
  isPrivate: boolean;
  adminReviewed: boolean;
  adminApproved: boolean;
  adminNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export const HD_TYPES: Record<HDType, { 
  name: string; 
  emoji: string;
  welcomeMessage: {
    fr: string;
    en: string;
  };
}> = {
  'manifestor': { 
    name: 'Manifestor', 
    emoji: '✨',
    welcomeMessage: {
      fr: "Bonjour, initiateur(trice) audacieux(se) ! Ton énergie est faite pour ouvrir la voie et impulser des changements. Suis ton intuition et informe les autres de tes intentions pour avancer avec sérénité. 💥",
      en: "Hello, bold initiator! Your energy is made to lead the way and drive change. Follow your intuition and inform others of your intentions to move forward with serenity. 💥"
    }
  },
  'generator': { 
    name: 'Generator', 
    emoji: '⚡️',
    welcomeMessage: {
      fr: "Salut, bâtisseur(euse) infatigable ! Ton énergie sacrée est une force inépuisable quand tu suis ce qui te passionne. Continue d'écouter ton cœur et laisse-toi guider par ton enthousiasme. 🚀",
      en: "Hi, tireless builder! Your sacral energy is an inexhaustible force when you follow your passions. Keep listening to your heart and let your enthusiasm guide you. 🚀"
    }
  },
  'manifesting-generator': { 
    name: 'Manifesting Generator', 
    emoji: '🌟',
    welcomeMessage: {
      fr: "Hello, créateur(trice) multi-tâches ! Ta capacité à jongler entre projets et idées est une véritable magie. Reste aligné(e) sur ce qui te fait vibrer et avance à ton rythme unique. 🌟",
      en: "Hello, multi-tasking creator! Your ability to juggle between projects and ideas is true magic. Stay aligned with what makes you vibrate and move at your unique pace. 🌟"
    }
  },
  'projector': { 
    name: 'Projector', 
    emoji: '💫',
    welcomeMessage: {
      fr: "Bienvenue, éclaireur précieux ! Ton rôle est d'illuminer le chemin des autres avec ta sagesse et ton intuition uniques. Écoute-toi, repose-toi quand il le faut, et sois prêt(e) à guider ceux qui te reconnaissent. ✨",
      en: "Welcome, precious guide! Your role is to illuminate the path for others with your unique wisdom and intuition. Listen to yourself, rest when needed, and be ready to guide those who recognize you. ✨"
    }
  },
  'reflector': { 
    name: 'Reflector', 
    emoji: '🌙',
    welcomeMessage: {
      fr: "Bienvenue, miroir précieux ! Ton énergie reflète l'environnement qui t'entoure. Prends le temps d'observer, de ressentir, et laisse-toi guider par la sagesse de tes cycles naturels. 🌙",
      en: "Welcome, precious mirror! Your energy reflects the environment around you. Take time to observe, feel, and let yourself be guided by the wisdom of your natural cycles. 🌙"
    }
  },
};

export interface Assessment {
  id: string;
  userId: string;
  date: Date;
  zone: 'green' | 'orange' | 'red';
  overallEnergy: number;
  affectedCenters: string[];
  symptoms: string[];
  response: string;
  notes?: string;
}

export interface Symptom {
  text: string;
  response: string;
  energyImpact: number; // Impact on overall energy level (-10 to +10)
}

export const SYMPTOMS_BY_TYPE: Record<HDType, Symptom[]> = {
  'projector': [
    { text: "Je me sens calme et aligné(e)", response: "Continue à suivre ton intuition et respecte ton rythme naturel.", energyImpact: 10 },
    { text: "Je ressens une légèreté dans tout mon corps", response: "Tu es parfaitement aligné(e) avec tes invitations et ton environnement. Garde cette fluidité.", energyImpact: 8 },
    { text: "Je suis inspiré(e) et prêt(e) à guider", response: "Utilise cette énergie pour partager ta sagesse avec ceux qui la reconnaissent.", energyImpact: 9 },
    { text: "Je ressens une fatigue ou un besoin de repos", response: "Accorde-toi une pause bien méritée pour recharger ton énergie.", energyImpact: -5 },
    { text: "J'ai des tensions dans les épaules ou le cou", response: "Relâche la pression et rappelle-toi que tu n'as rien à prouver.", energyImpact: -6 },
    { text: "Je ressens une anxiété diffuse", response: "Prends le temps de te recentrer et d'observer cette tension sans jugement.", energyImpact: -6 },
    { text: "Je me sens en surcharge émotionnelle", response: "Cette surcharge indique que tu as besoin de temps seul(e) pour traiter les énergies absorbées.", energyImpact: -7 },
    { text: "Je ressens une hypersensibilité aux émotions des autres", response: "Ta sensibilité est un don, mais elle nécessite des moments de retrait pour se recentrer.", energyImpact: -4 },
    { text: "Je me sens incompris(e) ou invisible", response: "C'est un signal que tu dois peut-être attendre la bonne invitation pour partager ta sagesse.", energyImpact: -5 },
    { text: "Je ressens une fatigue émotionnelle profonde", response: "Cette fatigue indique un besoin de te retirer pour recharger ton énergie.", energyImpact: -6 }
  ],
  'generator': [
    { text: "Je me sens satisfait(e) et énergisé(e)", response: "Continue sur cette voie, ton énergie sacrale est alignée avec ce que tu fais.", energyImpact: 10 },
    { text: "Je ressens une motivation intense", response: "Utilise cette énergie pour avancer sur des projets qui te passionnent.", energyImpact: 9 },
    { text: "Je suis enthousiaste à l'idée de commencer une tâche", response: "Suis cette excitation pour découvrir ce qui te procure satisfaction.", energyImpact: 8 },
    { text: "Je ressens de la frustration ou un blocage", response: "Analyse si cette tâche est alignée avec ton énergie sacrale. Ajuste si nécessaire.", energyImpact: -7 },
    { text: "Je suis fatigué(e) ou vidé(e)", response: "Fais une pause pour te recentrer. Ton énergie sacrale reviendra dès que tu seras reposé(e).", energyImpact: -8 },
    { text: "Je ressens une frustration ou de l'impatience", response: "Vérifie si tu suis vraiment ta réponse sacrale.", energyImpact: -5 },
    { text: "Je me sens bloqué(e) dans mon élan", response: "Reconnecte-toi à ton énergie sacrale. Qu'est-ce qui te fait vraiment vibrer maintenant ?", energyImpact: -6 },
    { text: "Je ressens une baisse de motivation", response: "C'est peut-être le moment de revenir à des activités qui te procurent une vraie satisfaction.", energyImpact: -4 },
    { text: "Je me sens déconnecté(e) de mon corps", response: "Ton corps est ta boussole. Prends le temps de te reconnecter à tes sensations physiques.", energyImpact: -5 },
    { text: "Je ressens une agitation intérieure", response: "Cette agitation peut être de l'énergie sacrale non utilisée.", energyImpact: -3 }
  ],
  'manifesting-generator': [
    { text: "Je me sens motivé(e) et débordant(e) d'énergie", response: "Utilise cette énergie pour avancer avec fluidité sur tes projets préférés.", energyImpact: 10 },
    { text: "Je ressens une satisfaction profonde dans ce que je fais", response: "Continue à suivre ton sacral et ton intuition, tu es en alignement.", energyImpact: 9 },
    { text: "Je suis excité(e) par un nouveau projet", response: "Prends le temps d'explorer ce projet avant de te lancer.", energyImpact: 8 },
    { text: "Je ressens de la frustration ou de l'impatience", response: "Réfléchis aux étapes oubliées. Ralentis et réécoute ton sacral.", energyImpact: -7 },
    { text: "Je suis fatigué(e) ou en surmenage", response: "Simplifie ton emploi du temps pour te concentrer sur l'essentiel.", energyImpact: -8 },
    { text: "Je me sens dispersé(e) dans trop de projets", response: "C'est normal d'avoir plusieurs centres d'intérêt, mais vérifie si chaque projet te procure vraiment de la satisfaction.", energyImpact: -4 },
    { text: "Je ressens de la culpabilité à switcher d'activité", response: "Ta nature est de passer rapidement d'une chose à l'autre. C'est ta façon d'être efficace.", energyImpact: -3 },
    { text: "Je me sens frustré(e) par la lenteur des autres", response: "Ton rythme est naturellement plus rapide. Trouve des façons d'utiliser cette énergie constructivement.", energyImpact: -4 },
    { text: "Je ressens un manque de focus", response: "Multi-tasker naturel, tu as besoin de variété. Organise tes activités pour alterner facilement.", energyImpact: -3 },
    { text: "Je me sens coupable de ne pas finir", response: "Ta nature est d'apprendre rapidement et de passer à autre chose. C'est ta force, pas une faiblesse.", energyImpact: -4 }
  ],
  'manifestor': [
    { text: "Je me sens en paix et dans le flux", response: "Continue à initier et à suivre ta voie, tu es parfaitement aligné(e).", energyImpact: 10 },
    { text: "Je ressens une liberté d'action", response: "Utilise cette liberté pour avancer sur des projets significatifs.", energyImpact: 9 },
    { text: "Je suis inspiré(e) et créatif(ve)", response: "Laisse ta créativité te guider et prends des initiatives alignées.", energyImpact: 8 },
    { text: "Je ressens de la colère ou de l'agitation", response: "Analyse les résistances et ajuste ton approche en informant les autres.", energyImpact: -7 },
    { text: "Je me sens bloqué(e) ou freiné(e)", response: "Reviens à tes intentions initiales et clarifie ce que tu veux vraiment.", energyImpact: -8 },
    { text: "Je ressens de la colère ou de la frustration", response: "Cette colère indique peut-être que tu n'as pas informé les autres de tes intentions.", energyImpact: -5 },
    { text: "Je me sens contraint(e) ou limité(e)", response: "Tu as besoin de liberté pour initier. Vérifie si tu informes suffisamment ton entourage.", energyImpact: -6 },
    { text: "Je ressens une pression à me justifier", response: "Tu n'as pas à te justifier, juste à informer. C'est différent.", energyImpact: -4 },
    { text: "Je me sens incompris(e) dans mes initiatives", response: "Prends le temps d'expliquer tes intentions, pas pour permission mais pour information.", energyImpact: -5 },
    { text: "Je ressens une résistance de l'entourage", response: "Cette résistance diminue quand tu prends le temps d'informer avant d'agir.", energyImpact: -5 }
  ],
  'reflector': [
    { text: "Je me sens harmonieux(se) et léger(e)", response: "Continue dans cet environnement nourrissant.", energyImpact: 10 },
    { text: "Je ressens un équilibre parfait avec mon environnement", response: "Profite de cet équilibre pour t'épanouir pleinement.", energyImpact: 9 },
    { text: "Je suis inspiré(e) par ce qui m'entoure", response: "Utilise cette inspiration pour explorer de nouvelles idées ou projets.", energyImpact: 8 },
    { text: "Je ressens une perturbation ou un désalignement", response: "Reviens à un environnement calme et observe les énergies extérieures.", energyImpact: -7 },
    { text: "Je me sens vidé(e) ou fatigué(e)", response: "Recharge-toi en t'isolant temporairement dans un lieu apaisant.", energyImpact: -8 },
    { text: "Je me sens submergé(e) par les émotions des autres", response: "Prends du recul pour distinguer ce qui t'appartient de ce que tu amplifies.", energyImpact: -6 },
    { text: "Je ressens une confusion identitaire", response: "Normal avec ton design. Donne-toi un cycle lunaire complet avant les grandes décisions.", energyImpact: -5 },
    { text: "Je me sens différent(e) ou inadapté(e)", response: "Ta différence est ta force. Tu es un miroir précieux pour les autres.", energyImpact: -4 },
    { text: "Je ressens un manque d'ancrage", response: "Trouve des repères stables dans ton environnement pour t'ancrer.", energyImpact: -5 },
    { text: "Je me sens désorienté(e)", response: "Observe comment l'environnement t'influence. Tu es comme la lune, en constant changement.", energyImpact: -4 }
  ]
};